import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import {
  CIDocForm,
  ClaveUnicaRetryScreen,
  QuestionsChallengeForm,
  VerifyClaveUnicaLogin,
} from "./components";
import { Modal, SnackBar } from "@components";
import {
  FormBody,
  FormContentContainer,
  FormHeader,
  FullScreenLoader,
} from "@components";
import {
  QuestionsChallengeActionError,
  ValidationChallengePayload,
  ValidationChallengeResponse,
  GetQuestionsFormData,
  CIDocFormData,
} from "@interfaces";
import { apiGateway, useDoctorContext } from "@contexts";
import { GetQuestions } from "./components/GetQuestions";
import {
  ErrorType,
  getEnrolmentErrorDisplay,
  onQuestionChallengeAxiosErrorInterpreter,
} from "./hooks/onQuestionChallengeError";
import {
  ENROLMENT_STEP,
  ENROLMENT_STEP_STATUS,
  useEnrolmentStatusContext,
} from "@contexts/EnrolmentStatusContext";
import { ClaveUnicaLogin } from "./components/ClaveUnicaLogin";
import { useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import {
  InlineError,
  useEnrolmentErrorHandler,
} from "./hooks/useEnrolmentErrorHandler";
import { tracker } from "@utils/openreplay";

const RENDERABLE_STEP_STATUSES: ENROLMENT_STEP_STATUS[] = [
  ENROLMENT_STEP_STATUS.NOT_STARTED,
  ENROLMENT_STEP_STATUS.STARTED,
];

type ClaveUnicaRetryState = {
  description?: string | React.ReactNode;
  nextStep?: ENROLMENT_STEP;
  nextStepStatus?: ENROLMENT_STEP_STATUS;
};

function claveUnicaRetryStateFrom(error: Error): ClaveUnicaRetryState {
  const { name, description } = getEnrolmentErrorDisplay(error);
  if (error instanceof QuestionsChallengeActionError) {
    return {
      description: description ?? name,
      nextStep: error.nextStep,
      nextStepStatus: error.nextStepStatus,
    };
  }
  const errorData = (error as AxiosError<ErrorType>).response?.data;
  return {
    description: description ?? name,
    nextStep: errorData?.nextStep,
    nextStepStatus: errorData?.nextStepStatus,
  };
}

export const ValidationChallengeScreen: React.FC = () => {
  const doctor = useDoctorContext();
  const location = useLocation();
  const { requestId } = location.state || {};
  const {
    currentStep,
    currentStepStatus,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
  } = useEnrolmentStatusContext();

  const [showUserIsVerifiedSnackbar, setShowUserIsVerifiedSnackbar] =
    useState(false);

  const [claveUnicaRetry, setClaveUnicaRetry] =
    useState<ClaveUnicaRetryState | null>(null);

  const [showValidateDoctorDataModal, setShowValidateDoctorDataModal] =
    useState(false);
  const [showValidateDoctorDataInputs, setShowValidateDoctorDataInputs] =
    useState(false);
  const [docNumErrorDescription, setDocNumErrorDescription] = useState<
    string | React.ReactNode | null
  >(null);

  const ciform = useForm<CIDocFormData>({
    defaultValues: {
      ciDocNum: "",
    },
  });

  const form = useForm<GetQuestionsFormData>({
    defaultValues: {
      name: doctor.name,
      firstSurname: doctor.lastName,
      secondSurname: "",
      birthdate: doctor.birthdate,
    },
  });

  const retryValidationChallengeRef = useRef<(() => void) | null>(null);

  const handleInvalidInput = useCallback(
    (error: InlineError) => {
      if (error.nextStep !== ENROLMENT_STEP.CI_DOC_NUM) return false;
      ciform.setError("ciDocNum", { type: "manual", message: error.name });
      setDocNumErrorDescription(error.description ?? null);
      return true;
    },
    [ciform],
  );

  const handleIncompleteData = useCallback(() => {
    setShowValidateDoctorDataModal(true);
    setShowValidateDoctorDataInputs(true);
    return true;
  }, []);

  const { handleEnrolmentError, enrolmentErrorModals } =
    useEnrolmentErrorHandler({
      onRetry: () => retryValidationChallengeRef.current?.(),
      onInvalidInput: handleInvalidInput,
      onIncompleteData: handleIncompleteData,
      fallbackTitle: "Error al generar preguntas",
    });

  const {
    mutate: createValidationChallenge,
    isPending: isCreatingValidationChallenge,
    data: questions,
  } = useMutation({
    mutationFn: async (
      validationChallengePayload: ValidationChallengePayload,
    ) => {
      const { questions } = await apiGateway<
        ValidationChallengeResponse,
        ErrorType
      >(
        {
          method: "post",
          url: "/enrolment/questions-challenge",
          data: validationChallengePayload,
        },
        onQuestionChallengeAxiosErrorInterpreter,
      );

      return questions;
    },
    onSuccess: async () => {
      setDocNumErrorDescription(null);
      await getEnrolmentStatus();
    },
    onError: handleEnrolmentError,
  });

  retryValidationChallengeRef.current = () => createValidationChallenge({});

  const handleClaveUnicaError = useCallback(
    async (error: Error) => {
      console.error("Error on clave unica step: ", error);
      const routesToAnotherStep =
        error instanceof QuestionsChallengeActionError &&
        error.action === "invalidInput" &&
        !!error.nextStep &&
        error.nextStep !== ENROLMENT_STEP.CLAVE_UNICA;
      if (routesToAnotherStep) {
        await handleEnrolmentError(error);
        return;
      }
      setClaveUnicaRetry(claveUnicaRetryStateFrom(error));
    },
    [handleEnrolmentError],
  );

  const handleClaveUnicaRetry = useCallback(async () => {
    const nextStep = claveUnicaRetry?.nextStep;
    const nextStepStatus = claveUnicaRetry?.nextStepStatus;
    setClaveUnicaRetry(null);
    if (nextStep && nextStepStatus) {
      setCurrentStep(nextStep);
      setCurrentStepStatus(nextStepStatus);
      return;
    }
    await getEnrolmentStatus();
  }, [
    claveUnicaRetry,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
  ]);

  useEffect(() => {
    const getEnrolmentStatusCallback = async () => {
      if (!currentStep) {
        await getEnrolmentStatus();
      }
    };
    getEnrolmentStatusCallback();
  }, [currentStep, getEnrolmentStatus]);

  const isRenderableStepStatus =
    currentStepStatus !== null &&
    RENDERABLE_STEP_STATUSES.includes(currentStepStatus);

  const stepStatusToRender = isRenderableStepStatus
    ? currentStepStatus
    : ENROLMENT_STEP_STATUS.NOT_STARTED;

  useEffect(() => {
    if (!currentStep || isRenderableStepStatus) return;
    console.error("Unhandled enrolment step status, rendering as NOT_STARTED", {
      step: currentStep,
      status: currentStepStatus,
    });
    tracker?.event("UnhandledEnrolmentStepStatus", {
      step: currentStep,
      status: currentStepStatus ?? "null",
    });
  }, [currentStep, currentStepStatus, isRenderableStepStatus]);

  const hasRecoveredOrphanedChallenge = useRef(false);

  useEffect(() => {
    if (
      currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED &&
      !questions &&
      !isCreatingValidationChallenge &&
      !hasRecoveredOrphanedChallenge.current
    ) {
      hasRecoveredOrphanedChallenge.current = true;
      createValidationChallenge({});
    }
  }, [
    currentStep,
    stepStatusToRender,
    questions,
    isCreatingValidationChallenge,
    createValidationChallenge,
  ]);

  if (claveUnicaRetry) {
    return (
      <ClaveUnicaRetryScreen
        description={claveUnicaRetry.description}
        onRetry={handleClaveUnicaRetry}
      />
    );
  }
  if (
    currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
    stepStatusToRender === ENROLMENT_STEP_STATUS.NOT_STARTED
  )
    return (
      <ClaveUnicaLogin requestId={requestId} onError={handleClaveUnicaError} />
    );
  if (
    currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
    stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED
  )
    return (
      <VerifyClaveUnicaLogin
        setShowUserIsVerifiedSnackbar={setShowUserIsVerifiedSnackbar}
        onError={handleClaveUnicaError}
      />
    );
  if (currentStep === ENROLMENT_STEP.CI_DOC_NUM)
    return (
      <>
        <CIDocForm
          docNumErrorDescription={docNumErrorDescription}
          form={ciform}
          onError={handleEnrolmentError}
        />
        {enrolmentErrorModals}
        <SnackBar
          snackBarState={{
            open: showUserIsVerifiedSnackbar,
            type: "success",
          }}
          onClose={() => setShowUserIsVerifiedSnackbar(false)}
          alerts={{
            success: "Identidad validada con clave única",
          }}
        />
      </>
    );
  if (
    currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
    stepStatusToRender === ENROLMENT_STEP_STATUS.NOT_STARTED
  ) {
    return (
      <>
        <GetQuestions
          form={form}
          showValidateDoctorDataInputs={showValidateDoctorDataInputs}
          createValidationChallenge={createValidationChallenge}
          isCreatingValidationChallenge={isCreatingValidationChallenge}
        />
        {enrolmentErrorModals}
        <Modal
          title="Se requiere información adicional"
          content="Algunos datos del Registro Nacional de Prestadores Individuales de Salud no fueron encontrados. Por favor, completa los nuevos campos requeridos."
          isOpen={showValidateDoctorDataModal}
          onClose={() => setShowValidateDoctorDataModal(false)}
          buttonProps={{
            buttonText: "Aceptar",
            onClick: () => setShowValidateDoctorDataModal(false),
          }}
        />
        <SnackBar
          snackBarState={{
            open: showUserIsVerifiedSnackbar,
            type: "success",
          }}
          onClose={() => setShowUserIsVerifiedSnackbar(false)}
          alerts={{
            success: "Identidad validada con clave única",
          }}
        />
      </>
    );
  }
  if (
    currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
    stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED
  ) {
    if (questions) {
      return (
        <QuestionsChallengeForm
          questions={questions}
          onError={handleEnrolmentError}
        />
      );
    }
    return (
      <>
        <FormHeader />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Cargando preguntas" />
          </FormBody>
        </FormContentContainer>
        {enrolmentErrorModals}
      </>
    );
  }
  return (
    <>
      <FormHeader />
      <FormContentContainer flex={1} disableGutters>
        <FormBody justifyContent="center">
          <FullScreenLoader title="Cargando" />
        </FormBody>
      </FormContentContainer>
    </>
  );
};
